import * as React from "react";
import { useNavigate } from "react-router-dom";

import { fetchVendorDetailsAndSetVendor } from "@bcc/routes/admin-roasters-page.controller";
import { getNavigationTarget } from "@bcc/routes/admin-roasters-page.utils";
import useVendorContext from "@client-shared/providers/vendor";

import { useClearAllState } from "./use-clear-all-state";

interface UseAdminImpersonationResult {
  handleImpersonate: (vendorKey: string) => Promise<void>;
  isSelectingVendor: boolean;
  selectionError: string | undefined;
}

/**
 * Encapsulates the admin impersonation side-effect flow:
 * 1. Clear vendor data and cache while preserving the admin session
 * 2. Fetch vendor details and set vendor context for the current session
 * 3. Navigate to dashboard
 *
 * Keeps the page component free of orchestration logic.
 */
export function useAdminImpersonation(): UseAdminImpersonationResult {
  const navigate = useNavigate();
  const [, vendorDispatch, vendorUtils] = useVendorContext();
  const clearAllState = useClearAllState();
  const [selectionError, setSelectionError] = React.useState<string | undefined>(undefined);
  const [isSelectingVendor, setIsSelectingVendor] = React.useState(false);

  const handleImpersonate = React.useCallback(
    async (vendorKey: string) => {
      setSelectionError(undefined);
      setIsSelectingVendor(true);
      try {
        clearAllState("vendor-change");
        const sessionScope = vendorUtils?.createSessionScope?.();
        await fetchVendorDetailsAndSetVendor(vendorKey, sessionScope?.dispatch ?? vendorDispatch);
        if (sessionScope && !sessionScope.isActive()) return;
        navigate(getNavigationTarget());
      } catch (selectionFetchError) {
        const errorMessage = selectionFetchError instanceof Error ? selectionFetchError.message : "Unknown error";
        setSelectionError(errorMessage);
      } finally {
        setIsSelectingVendor(false);
      }
    },
    [navigate, vendorDispatch, vendorUtils, clearAllState]
  );

  return { handleImpersonate, isSelectingVendor, selectionError };
}
